import { useEffect, useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { publicApi } from '@/lib/api';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '@/lib/i18n';

export default function Gallery() {
  const [photos, setPhotos] = useState([]);
  const [filter, setFilter] = useState('all');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const { t } = useLanguage();

  const categories = [
    { value: 'all', label: t('gallery.all') },
    { value: 'plats', label: t('gallery.plats') },
    { value: 'terrasse', label: t('gallery.terrasse') },
    { value: 'salle', label: t('gallery.salle') },
    { value: 'ambiance', label: t('gallery.ambiance') },
  ];

  useEffect(() => {
    publicApi.getPhotos().then(r => setPhotos(r.data)).catch(() => {});
  }, []);

  const filtered = filter === 'all' ? photos : photos.filter(p => p.category === filter);

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };

  const navigate = (dir) => {
    setCurrentIndex((prev) => (prev + dir + filtered.length) % filtered.length);
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="gallery-page">
      <Navbar />
      <div className="pt-28 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <span className="text-sm font-medium tracking-wide uppercase text-garden-500 mb-2 block">{t('gallery.sectionLabel')}</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-garden-800 mb-4">
            {t('gallery.title')}
          </h1>
          <p className="text-base md:text-lg text-charcoal/70 max-w-2xl">
            {t('gallery.description')}
          </p>
        </div>

        {/* Filters */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <div className="flex gap-2 overflow-x-auto pb-2">
            {categories.map((cat) => (
              <button
                key={cat.value}
                data-testid={`gallery-filter-${cat.value}`}
                onClick={() => setFilter(cat.value)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all shrink-0 ${
                  filter === cat.value
                    ? 'bg-garden-800 text-white'
                    : 'bg-white text-charcoal hover:bg-garden-100 border border-garden-200'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Photo Grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filtered.map((photo, i) => (
              <div
                key={photo.id}
                className="gallery-item relative rounded-2xl overflow-hidden aspect-square group"
                onClick={() => openLightbox(i)}
                data-testid={`gallery-photo-${photo.id}`}
              >
                <img src={photo.url} alt={photo.alt} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-garden-800/0 group-hover:bg-garden-800/30 transition-colors flex items-end">
                  <span className="text-white text-sm font-medium p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    {photo.alt}
                  </span>
                </div>
              </div>
            ))}
          </div>
          {filtered.length === 0 && (
            <p className="text-center text-charcoal/50 py-12">{t('gallery.empty')}</p>
          )}
        </div>
      </div>

      {/* Lightbox */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-4xl bg-black/95 border-none p-0 overflow-hidden" data-testid="gallery-lightbox">
          <DialogTitle className="sr-only">{t('gallery.lightboxTitle')}</DialogTitle>
          {filtered[currentIndex] && (
            <div className="relative">
              <img
                src={filtered[currentIndex].url}
                alt={filtered[currentIndex].alt}
                className="w-full max-h-[80vh] object-contain"
              />
              <button
                onClick={() => navigate(-1)}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white rounded-full p-2 transition-colors"
                data-testid="gallery-lightbox-prev"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={() => navigate(1)}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white rounded-full p-2 transition-colors"
                data-testid="gallery-lightbox-next"
              >
                <ChevronRight size={24} />
              </button>
              <div className="absolute bottom-4 left-0 right-0 text-center">
                <span className="text-white/80 text-sm bg-black/50 px-4 py-2 rounded-full">
                  {filtered[currentIndex].alt} — {currentIndex + 1}/{filtered.length}
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
